import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { PASSWORD_MIN_LENGTH, storeAuth } from '../auth';
import type { AuthResponse } from '../types';
import { useUser } from '../providers/UserProvider';
import * as authRepository from '../repositories/authRepository';
import { clearReaderAction, resumeReaderAction } from '../utils/readerPendingAction';
import { IconCheck } from './icons';
import { ModalOverlay } from './ModalOverlay';
import { readerEvent } from '../utils/readerTelemetry';

type AuthDialogAction = 'mode_switch' | 'close' | 'authenticated' | 'resume_failed' | 'resume_skip';
const authDialogEvent = (action: AuthDialogAction, creating: boolean) =>
  readerEvent('auth_dialog', { action, mode: creating ? 'signup' : 'login' });

// Google's multicolor "G" mark; decorative, the button text names the action.
function GoogleIcon() {
  return <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>;
}

export function ReaderAuthDialog({ signup = false }: { signup?: boolean }) {
  const { isLoggedIn, login } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from;
  const redirectTo = from?.pathname ? `${from.pathname}${from.search || ''}` : '/';
  const [creating, setCreating] = useState(signup);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [message, setMessage] = useState(location.state?.message || '');
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState<AuthResponse | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [resumeFailed, setResumeFailed] = useState(false);
  const dialog = useRef<HTMLDivElement>(null);
  const close = useCallback(() => {
    if (busy) return;
    if (data) { if (resumeFailed) skipAction(); else void finish(); return; }
    authDialogEvent('close', creating);
    clearReaderAction();
    navigate(location.state?.backgroundLocation || '/', { replace: true });
  }, [busy, data, resumeFailed, creating, location.state, navigate]);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden'; dialog.current?.focus();
    return () => { document.body.style.overflow = overflow; previous?.focus(); };
  }, []);
  if (isLoggedIn && !data) return <Navigate to={redirectTo} replace />;
  async function finish() {
    if (!data || busy) return;
    setBusy(true); setMessage('');
    try {
      await resumeReaderAction();
      login(data); navigate(redirectTo, { replace: true });
    } catch {
      setResumeFailed(true);
      authDialogEvent('resume_failed', creating);
      setMessage('Your account is ready, but we could not finish your action. Try again, or continue without it.');
    } finally { setBusy(false); }
  }
  // A pending action that keeps failing must not trap an authenticated reader in the dialog.
  function skipAction() {
    if (!data) return;
    authDialogEvent('resume_skip', creating);
    clearReaderAction(); login(data); navigate(redirectTo, { replace: true });
  }
  function switchMode() {
    authDialogEvent('mode_switch', !creating);
    setCreating(value => !value); setPassword(''); setShowPassword(false);
    setEmailError(''); setPasswordError(''); setMessage('');
  }
  function continueWithGoogle() {
    // A navigation, not a form post: the CSP's form-action 'self' would block
    // a form submitting to Google.
    setBusy(true);
    window.location.assign(authRepository.googleSignInUrl(redirectTo, false));
  }
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (busy) return;
    const invalidEmail = !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    const invalidPassword = creating ? password.length < PASSWORD_MIN_LENGTH : !password;
    setEmailError(invalidEmail ? 'Enter a valid email address.' : '');
    setPasswordError(invalidPassword ? (creating ? 'Use at least 8 characters.' : 'Enter your password.') : '');
    if (invalidEmail || invalidPassword) return;
    setBusy(true); setMessage('');
    try {
      const response = creating ? await authRepository.signup(undefined, email.trim(), password) : await authRepository.login(email.trim(), password);
      storeAuth(response); setData(response); setPassword('');
      authDialogEvent('authenticated', creating);
    } catch (error) {
      const text = (error as Error).message;
      if (/already|exists|registered|in use/i.test(text)) setEmailError('An account with this email already exists. Log in instead.');
      else setMessage(text);
    } finally { setBusy(false); }
  }
  const title = data ? (creating ? 'Account created' : 'Signed in') : creating ? 'Create your account' : 'Log in';
  return <ModalOverlay className="reader-auth-dialog" onClose={close}>
    <div ref={dialog} role="dialog" aria-modal="true" aria-labelledby="reader-auth-title" tabIndex={-1} onKeyDown={event => {
      if (event.key !== 'Tab') return;
      const controls = Array.from(dialog.current!.querySelectorAll<HTMLElement>('a[href], button:not(:disabled), input:not(:disabled)'));
      const first = controls[0], last = controls[controls.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialog.current)) { event.preventDefault(); first?.focus(); }
    }}>
      <div className="reader-auth-heading"><h1 id="reader-auth-title">{title}</h1><button type="button" className="reader-auth-close" aria-label="Close sign in" disabled={busy} onClick={close}>×</button></div>
      <div className="reader-auth-brand">TLDR</div>
      {data ? <><span className="reader-auth-done-icon" aria-hidden="true"><IconCheck size={20} /></span><p>{creating ? `Your account is ready for ${email.trim()}.` : `You are signed in as ${email.trim()}.`}</p>
        <button type="button" className="reader-auth-submit" disabled={busy} onClick={() => void finish()}>{resumeFailed ? 'Try again' : 'Continue to your feed'}</button>
        {resumeFailed && <button type="button" className="reader-auth-skip" disabled={busy} onClick={skipAction}>Continue without it</button>}</> : <>
        <p className="reader-auth-switch">{creating ? 'Already have an account? ' : 'New to TLDR? '}<button type="button" onClick={switchMode}>{creating ? 'Log in' : 'Sign up'}</button></p>
        <form noValidate className="reader-auth-form" onSubmit={event => void submit(event)}>
          <input name="identity" type="email" placeholder="Email" aria-label="Email" autoComplete="email" value={email} onChange={event => { setEmail(event.target.value); setEmailError(''); }} aria-invalid={!!emailError} aria-describedby={emailError ? 'reader-email-error' : undefined} disabled={busy} />
          {emailError && <p id="reader-email-error" className="reader-auth-error" role="alert">{emailError}</p>}
          <div className="reader-auth-password"><input name="password" type={showPassword ? 'text' : 'password'} placeholder={creating ? 'Create a password' : 'Password'} aria-label={creating ? 'Create a password' : 'Password'} autoComplete={creating ? 'new-password' : 'current-password'} value={password} onChange={event => { setPassword(event.target.value); setPasswordError(''); }} aria-invalid={!!passwordError} aria-describedby={passwordError ? 'reader-password-error' : creating ? 'reader-password-help' : undefined} disabled={busy} />
            <button type="button" aria-label={showPassword ? 'Hide password' : 'Show password'} onClick={() => setShowPassword(value => !value)}>{showPassword ? 'Hide' : 'Show'}</button></div>
          {passwordError && <p id="reader-password-error" className="reader-auth-error" role="alert">{passwordError}</p>}
          {creating && <p id="reader-password-help" className="reader-auth-help">At least 8 characters.</p>}
          <button type="submit" className="reader-auth-submit" disabled={busy}>{busy ? 'Please wait…' : creating ? 'Create account' : 'Log in'}</button>
          {!creating && <Link className="reader-auth-forgot" to="/forgot-password">Forgot password?</Link>}
        </form>
        <div className="reader-auth-divider">or</div>
        <button type="button" className="reader-auth-google-btn" disabled={busy} onClick={continueWithGoogle}><GoogleIcon />Continue with Google</button>
        {creating && <p className="reader-auth-terms">By creating an account you agree to TLDR's <a href="https://tldr.tech/terms" target="_blank" rel="noreferrer">Terms</a> and <a href="https://tldr.tech/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>.</p>}
      </>}
      {message && <p className="reader-auth-error" role="alert">{message}</p>}
    </div>
  </ModalOverlay>;
}
